import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ApiResponse, UserMatch, MatchUser, MatchTier } from '@/api/types';
import {
  Users,
  Loader2,
  ChevronRight,
  Sparkles,
  Zap,
  Star,
  Circle,
  Send,
  RefreshCw,
} from 'lucide-react';
import clsx from 'clsx';
import WaveModal from '@/components/WaveModal';

interface MatchesResponse {
  matches: UserMatch[];
  pagination: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

const TIER_CONFIG: Record<MatchTier, { label: string; icon: React.ElementType; color: string; bgColor: string }> = {
  twins: { label: 'Twins', icon: Sparkles, color: 'text-twins', bgColor: 'bg-twins-light' },
  strong: { label: 'Strong', icon: Zap, color: 'text-strong', bgColor: 'bg-strong-light' },
  moderate: { label: 'Moderate', icon: Star, color: 'text-moderate', bgColor: 'bg-moderate-light' },
  low: { label: 'Low', icon: Circle, color: 'text-low', bgColor: 'bg-low-light' },
};

// From the match's relationship, which the server decides with the same rule
// as the wave endpoint. `respond_wave` counts: waving back makes it mutual.
function canWave(match: UserMatch) {
  const actions = match.relationship?.allowedActions ?? [];
  return actions.includes('wave') || actions.includes('respond_wave');
}

// "Roots in Gujarat (Amreli), Kerala · Raised in Rajkot", from the match's
// identity. Nothing when the member's profile is hidden from this viewer.
function identityLine(user: MatchUser): string | null {
  const identity = user.identity;
  if (!identity) return null;
  // The server's own line, drawn as sent: clients never compose it.
  if (identity.declaration) return identity.declaration.line;
  const roots = identity.roots.map((root) => (root.place ? `${root.name} (${root.place})` : root.name));
  // Every childhood (grewUp), falling back to the first (raisedIn) for an older API.
  const childhoods = (identity.grewUp ?? (identity.raisedIn ? [{ place: identity.raisedIn.city }] : []))
    .map((c) => c.place)
    .filter((place): place is string => Boolean(place));
  const parts = [
    roots.length ? `Roots in ${roots.join(', ')}` : null,
    childhoods.length ? `Raised in ${childhoods.join(', ')}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

// What to show where no wave can be sent. Nothing for `none`: a member whose
// wave was declined sees exactly what they see for anyone they cannot wave at.
function waveLabel(match: UserMatch): string | null {
  const { waveState, waveRetryAfter } = match.relationship ?? {};
  switch (waveState) {
    case 'pending_sent':
      return 'Wave sent';
    case 'mutual':
      return 'Matched';
    case 'declined':
      return 'You declined';
    case 'expired':
      return waveRetryAfter
        ? `Wave again from ${new Date(waveRetryAfter).toLocaleDateString()}`
        : 'Wave expired';
    default:
      return null;
  }
}

export default function Matches() {
  const [activeTier, setActiveTier] = useState<MatchTier | 'all'>('all');
  const [waveTarget, setWaveTarget] = useState<MatchUser | null>(null);
  const queryClient = useQueryClient();

  // Fetch matches
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ['matches', activeTier],
    queryFn: ({ pageParam = 0 }) => {
      const params = new URLSearchParams({
        offset: String(pageParam),
        limit: '20',
      });
      if (activeTier !== 'all') {
        params.set('tier', activeTier);
      }
      return api.get<ApiResponse<MatchesResponse>>(`/identity-board/discovery/matches?${params}`);
    },
    getNextPageParam: (lastPage, allPages) => {
      const pagination = lastPage.data?.pagination;
      if (pagination?.hasMore) {
        return allPages.length * 20; // offset
      }
      return undefined;
    },
    initialPageParam: 0,
  });

  // Compute matches mutation
  const computeMatches = useMutation({
    mutationFn: () => api.post<ApiResponse<{ computed: number }>>('/identity-board/discovery/compute-matches'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
    },
  });

  const allMatches = data?.pages.flatMap((page) => page.data?.matches || []) || [];

  // Count by tier from all matches
  const tierCounts = allMatches.reduce(
    (acc, match) => {
      acc[match.matchTier] = (acc[match.matchTier] || 0) + 1;
      return acc;
    },
    {} as Record<MatchTier, number>
  );

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold">Discover Matches</h1>
          <p className="text-gray-600">
            People who share your background and interests
          </p>
        </div>
        <button
          onClick={() => computeMatches.mutate()}
          disabled={computeMatches.isPending}
          className="btn-outline flex items-center gap-2"
        >
          {computeMatches.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Refresh Matches
        </button>
      </div>

      {/* Tier Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveTier('all')}
          className={clsx(
            'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
            activeTier === 'all'
              ? 'bg-gray-900 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          )}
        >
          All Matches
        </button>
        {(['twins', 'strong', 'moderate', 'low'] as MatchTier[]).map((tier) => {
          const config = TIER_CONFIG[tier];
          const Icon = config.icon;
          return (
            <button
              key={tier}
              onClick={() => setActiveTier(tier)}
              className={clsx(
                'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2',
                activeTier === tier
                  ? `${config.bgColor} ${config.color}`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              )}
            >
              <Icon className="w-4 h-4" />
              {config.label}
              {tierCounts[tier] > 0 && (
                <span className="text-xs opacity-75">{tierCounts[tier]}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && allMatches.length === 0 && (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No matches yet</h3>
          <p className="text-gray-600 mb-6">
            Complete more of your Identity Board to unlock matches!
          </p>
          <Link to="/lab/identity-board" className="btn-primary">
            Complete Your Board
          </Link>
        </div>
      )}

      {/* Matches Grid */}
      {allMatches.length > 0 && (
        <div className="space-y-3">
          {allMatches.map((match) => {
            // Fallback so an unknown future tier degrades to a plain badge
            // instead of crashing the whole page
            const tierConfig = TIER_CONFIG[match.matchTier] ?? TIER_CONFIG.low;
            const TierIcon = tierConfig.icon;
            const matchedUser = match.user;

            return (
              <Link
                key={match.matchId}
                to={`/lab/identity-board/discovery/${matchedUser.id}`}
                className="card p-4 flex items-center gap-4 hover:shadow-md transition-shadow group"
              >
                {/* Avatar */}
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                  {matchedUser.name?.charAt(0) || '?'}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold truncate">{matchedUser.name}</h3>
                    <span className={clsx(
                      'px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1',
                      tierConfig.bgColor,
                      tierConfig.color
                    )}>
                      <TierIcon className="w-3 h-3" />
                      {Math.round(match.overallScore)}%
                    </span>
                  </div>

                  {/* Their identity, as their Parichay shows it to you */}
                  {identityLine(matchedUser) && (
                    <p className="text-xs text-gray-500 mb-1">{identityLine(matchedUser)}</p>
                  )}

                  {/* Shared traits preview */}
                  {match.sharedTraits && match.sharedTraits.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {match.sharedTraits.slice(0, 3).map((trait, i) => (
                        <span
                          key={i}
                          className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded"
                        >
                          {trait.label}
                        </span>
                      ))}
                      {match.sharedTraits.length > 3 && (
                        <span className="text-xs text-gray-500">
                          +{match.sharedTraits.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Wave button — only where the server accepts a wave. It used
                    to show on every card, and the server refused many of them. */}
                {canWave(match) ? (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setWaveTarget(match.user);
                    }}
                    className="btn-outline px-3 py-1.5 text-sm flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Send className="w-3 h-3" />
                    {match.relationship.allowedActions.includes('respond_wave') ? 'Wave back' : 'Wave'}
                  </button>
                ) : (
                  waveLabel(match) && (
                    <span className="text-xs text-gray-500 whitespace-nowrap">{waveLabel(match)}</span>
                  )
                )}

                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
              </Link>
            );
          })}
        </div>
      )}

      {/* Load More */}
      {hasNextPage && (
        <div className="text-center mt-6">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="btn-outline"
          >
            {isFetchingNextPage ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}

      {/* Refetching indicator */}
      {isRefetching && !isLoading && (
        <div className="fixed bottom-4 right-4 bg-gray-900 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Refreshing...
        </div>
      )}

      {/* Wave modal — opened from the inline Wave button on each match card */}
      {waveTarget && (
        <WaveModal
          toUserId={waveTarget.id}
          toUserName={waveTarget.name}
          open={true}
          onClose={() => setWaveTarget(null)}
        />
      )}
    </div>
  );
}
